import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { ArrowRight, Calendar, Calculator, Trash2, FileDown, FileText, Eye } from 'lucide-react';
import { addDailyLog, getSuppliers, getDailyLogs, updateDailyLog, getPaymentMethods, getDealClosures, getPurchaseInvoices, getPurchaseInvoiceDownloadUrl, getBankAccounts, getCustomers, addCustomer, getCustomerBalance, getPriceItems, addPriceItem } from '../api';
import { toast } from '../hooks/use-toast';
import InvoiceGallery from '../components/InvoiceGallery';
import BusinessCustomerDialog from '../components/BusinessCustomerDialog';
import PrivateCustomerDialog from '../components/PrivateCustomerDialog';
import PaymentManager from '../components/PaymentManager';
import { findChecksMissingDate, findPaymentsMissingMethod } from '../lib/paymentValidation';
import { FloatingDropdown } from '../components/FloatingDropdown';
import { matchesCustomer } from '../lib/customerSearch';
import { useRecordVat, withVat, useExemptCustomerVat } from '../lib/vat';
import { VatSummary } from '../components/VatSummary';
import { PrintButtons } from '../components/PrintButtons';

// Utility function to generate UUID
const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

const DailyLog = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('id');
  const [isEditMode, setIsEditMode] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [dealClosures, setDealClosures] = useState([]);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [consolidatedPaidNote, setConsolidatedPaidNote] = useState('');
  
  const [formData, setFormData] = useState({
    customerName: '',
    date: (() => {
      const today = new Date();
      return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    })(),
    payment: '',
    accountClosed: false
  });

  const [payments, setPayments] = useState([
    { id: '1', method: '', amount: '', notes: '', checkNumber: '', checkDate: '', bankName: '', branch: '', accountNumber: '', paymentDate: '', hasReceipt: false }
  ]);
  
  const [workItems, setWorkItems] = useState([
    { id: generateUUID(), partName: '', quantity: '', costPrice: '', transactionAmount: '', profit: '', hasInvoice: false, invoiceNumber: '', supplier: '' }
  ]);

  // Global price list (מחירון כללי) for part name autocomplete
  const GLOBAL_PRICE_LIST_ID = '__global__';
  const [priceItems, setPriceItems] = useState([]);
  const [activePartRow, setActivePartRow] = useState(null);
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Invoice gallery state
  const [invoiceGalleryOpen, setInvoiceGalleryOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  
  // Purchase invoices state
  const [purchaseInvoices, setPurchaseInvoices] = useState([]);
  const [isInvoiceSelectOpen, setIsInvoiceSelectOpen] = useState(false);
  const [selectedWorkItemForInvoice, setSelectedWorkItemForInvoice] = useState(null);

  // Customer dialogs state
  const [isBusinessCustomerDialogOpen, setIsBusinessCustomerDialogOpen] = useState(false);
  const [isPrivateCustomerDialogOpen, setIsPrivateCustomerDialogOpen] = useState(false);

  // Customer autocomplete state
  const [allCustomers, setAllCustomers] = useState([]);
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [customerBalanceInfo, setCustomerBalanceInfo] = useState(null);

  // Calculator state
  const [isCalculatorOpen, setIsCalculatorOpen] = useState(false);
  const [calcDisplay, setCalcDisplay] = useState('0');
  const [calcPrevValue, setCalcPrevValue] = useState(null);
  const [calcOperation, setCalcOperation] = useState(null);
  const [calcNewNumber, setCalcNewNumber] = useState(true);

  useEffect(() => {
    loadSuppliers();
    loadPaymentMethods();
    loadPurchaseInvoices();
    loadBankAccounts();
    loadAllCustomers();
    getPriceItems(GLOBAL_PRICE_LIST_ID).then(data => setPriceItems(data || [])).catch(() => setPriceItems([]));
    if (editId) {
      loadDailyLogData();
    } else if (searchParams.get('customer')) {
      setFormData(prev => ({ ...prev, customerName: searchParams.get('customer') }));
    }
  }, [editId]);

  const loadAllCustomers = async () => {
    try {
      const data = await getCustomers();
      setAllCustomers(data);
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const loadBankAccounts = async () => {
    try {
      const data = await getBankAccounts();
      setBankAccounts(data);
    } catch (error) {
      console.error('Error loading bank accounts:', error);
    }
  };

  // Keyboard shortcuts for customer dialogs
  useEffect(() => {
    const handleKeyDown = (e) => {
      // F9 - Business Customer
      if (e.key === 'F9') {
        e.preventDefault();
        setIsBusinessCustomerDialogOpen(true);
      }
      // F10 - Private Customer
      if (e.key === 'F10') {
        e.preventDefault();
        setIsPrivateCustomerDialogOpen(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Track form changes and prevent accidental exit
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  const loadPurchaseInvoices = async () => {
    try {
      const data = await getPurchaseInvoices();
      setPurchaseInvoices(data);
    } catch (error) {
      console.error('Error loading purchase invoices:', error);
    }
  };

  const vatState = useRecordVat();
  useExemptCustomerVat(vatState, formData.customerName, !editId);
  const subtotalAmount = () => parseFloat(calculateTotalAmount()) || 0;

  const loadDailyLogData = async () => {
    try {
      const logs = await getDailyLogs();
      const log = logs.find(l => l.id === editId);
      if (log) {
        setIsEditMode(true);
        vatState.loadFromRecord(log);
        setFormData({
          customerName: log.customerName,
          date: log.date,
          payment: log.payment || '',
          accountClosed: log.accountClosed || false
        });
        if (log.workItems && log.workItems.length > 0) {
          setWorkItems(log.workItems);
        }
        if (log.payments && log.payments.length > 0) {
          setPayments(log.payments);
        }
        if (log.consolidatedPaidNote) {
          setConsolidatedPaidNote(log.consolidatedPaidNote);
        }
      }
    } catch (error) {
      console.error('Error loading daily log:', error);
      toast({
        title: 'שגיאה',
        description: 'לא ניתן לטעון את הרישום',
        variant: 'destructive'
      });
    }
  };

  const loadSuppliers = async () => {
    try {
      const data = await getSuppliers();
      setSuppliers(data);
    } catch (error) {
      console.error('Error loading suppliers:', error);
    }
  };

  const loadPaymentMethods = async () => {
    try {
      const data = await getPaymentMethods();
      setPaymentMethods(data);
    } catch (error) {
      console.error('Error loading payment methods:', error);
    }
  };

  const loadDealClosures = async () => {
    try {
      const data = await getDealClosures();
      setDealClosures(data);
    } catch (error) {
      console.error('Error loading deal closures:', error);
    }
  };

  const handleImportFromDealClosure = (deal) => {
    // Set customer name and date
    setFormData({
      ...formData,
      customerName: deal.customerName,
      date: deal.date
    });

    // Import work items from deal with profit calculation
    const importedItems = deal.items.map((item, index) => {
      const costPrice = parseFloat(item.costPrice) || 0;
      const transactionAmount = parseFloat(item.customerPrice || item.total) || 0;
      const quantity = parseFloat(item.quantity) || 0;
      const profit = ((transactionAmount - costPrice) * quantity).toFixed(2);
      
      return {
        id: generateUUID(),
        partName: item.partName || '',
        quantity: item.quantity || '',
        costPrice: item.costPrice || '',
        transactionAmount: item.customerPrice || item.total || '',
        profit: profit,
        hasInvoice: false,
        invoiceNumber: '',
        supplier: item.supplier || ''
      };
    });

    setWorkItems(importedItems);

    // Close dialog
    setIsImportDialogOpen(false);

    toast({
      title: 'הצלחה!',
      description: 'הנתונים יובאו בהצלחה מסגירת העסקה',
    });
  };

  // Calculator functions
  const handleCalcNumber = (num) => {
    if (calcNewNumber) {
      setCalcDisplay(num.toString());
      setCalcNewNumber(false);
    } else {
      setCalcDisplay(calcDisplay === '0' ? num.toString() : calcDisplay + num);
    }
  };

  const handleCalcOperation = (op) => {
    const current = parseFloat(calcDisplay);
    
    if (calcPrevValue === null) {
      setCalcPrevValue(current);
    } else if (calcOperation) {
      const result = calculateResult();
      setCalcDisplay(result.toString());
      setCalcPrevValue(result);
    }
    
    setCalcNewNumber(true);
    setCalcOperation(op);
  };

  const calculateResult = () => {
    const prev = calcPrevValue;
    const current = parseFloat(calcDisplay);
    
    switch (calcOperation) {
      case '+':
        return prev + current;
      case '-':
        return prev - current;
      case '*':
        return prev * current;
      case '/':
        return prev / current;
      default:
        return current;
    }
  };

  const handleSelectBusinessCustomer = (customerName) => {
    setFormData({
      ...formData,
      customerName: customerName
    });
  };

  const handleSelectPrivateCustomer = (customerName) => {
    setFormData({
      ...formData,
      customerName: customerName
    });
  };

  const handleNavigation = (path) => {
    if (isDirty) {
      setPendingNavigation(path);
      setShowExitDialog(true);
    } else {
      navigate(path);
    }
  };

  const confirmExit = () => {
    setIsDirty(false);
    setShowExitDialog(false);
    if (pendingNavigation) {
      navigate(pendingNavigation);
    }
  };

  const cancelExit = () => {
    setShowExitDialog(false);
    setPendingNavigation(null);
  };

  const handleCalcEquals = () => {
    if (calcOperation && calcPrevValue !== null) {
      const result = calculateResult();
      setCalcDisplay(result.toString());
      setCalcPrevValue(null);
      setCalcOperation(null);
      setCalcNewNumber(true);
    }
  };

  const handleCalcClear = () => {
    setCalcDisplay('0');
    setCalcPrevValue(null);
    setCalcOperation(null);
    setCalcNewNumber(true);
  };

  const handleCalcDecimal = () => {
    if (!calcDisplay.includes('.')) {
      setCalcDisplay(calcDisplay + '.');
      setCalcNewNumber(false);
    }
  };

  // Work Items functions
  const selectPriceItem = (itemId, pi) => {
    setIsDirty(true);
    setWorkItems(prev => {
      const updated = prev.map(item => {
        if (item.id !== itemId) return item;
        let quantity = parseFloat(item.quantity) || 0;
        if (!item.quantity || quantity === 0) quantity = 1;
        const transaction = parseFloat(pi.price) || 0;
        const cost = parseFloat(pi.costPrice) || parseFloat(item.costPrice) || 0;
        const profit = ((transaction - cost) * quantity).toFixed(2);
        return {
          ...item,
          partName: pi.partName,
          quantity: String(quantity),
          costPrice: pi.costPrice ? String(pi.costPrice) : item.costPrice,
          transactionAmount: pi.price ? String(pi.price) : item.transactionAmount,
          profit,
          supplier: pi.supplier || item.supplier
        };
      });
      const last = updated[updated.length - 1];
      if ((last.partName || last.quantity || last.costPrice || last.transactionAmount) && updated.length === prev.length) {
        return [...updated, { id: generateUUID(), partName: '', quantity: '', costPrice: '', transactionAmount: '', profit: '', hasInvoice: false, invoiceNumber: '', supplier: '' }];
      }
      return updated;
    });
    setActivePartRow(null);
  };

  const addItemToPriceList = async (item) => {
    if (!item.partName || !item.partName.trim()) return;
    try {
      const added = await addPriceItem(GLOBAL_PRICE_LIST_ID, {
        partName: item.partName.trim(),
        price: item.transactionAmount || '',
        costPrice: item.costPrice || '',
        supplier: item.supplier || '',
        invoiceId: '',
        date: ''
      });
      setPriceItems(prev => [...prev, added]);
      setActivePartRow(null);
      toast({ title: 'נוסף למחירון', description: `"${item.partName.trim()}" נוסף למחירון הכללי` });
    } catch (error) {
      toast({ title: 'שגיאה', description: 'לא ניתן להוסיף למחירון', variant: 'destructive' });
    }
  };

  const handleItemChange = (id, field, value) => {
    setIsDirty(true);
    const updatedItems = workItems.map(item => {
      if (item.id === id) {
        const updated = { ...item, [field]: value };

        // Default quantity to 1 when a price/part is entered but quantity left empty
        if ((field === 'transactionAmount' || field === 'costPrice' || field === 'partName') && value && (updated.quantity === '' || updated.quantity === undefined || updated.quantity === null)) {
          updated.quantity = '1';
        }

        // Auto-calculate profit when costPrice, transactionAmount or quantity changes
        if (field === 'costPrice' || field === 'transactionAmount' || field === 'quantity' || field === 'partName') {
          const transaction = parseFloat(updated.transactionAmount) || 0;
          const cost = parseFloat(updated.costPrice) || 0;
          const quantity = parseFloat(updated.quantity) || 0;
          updated.profit = ((transaction - cost) * quantity).toFixed(2);
        }

        return updated;
      }
      return item;
    });
    setWorkItems(updatedItems);
    
    // Auto-add new row if last row is being filled
    const lastItem = updatedItems[updatedItems.length - 1];
    const isLastItemFilled = lastItem.partName || lastItem.quantity || lastItem.costPrice || lastItem.transactionAmount;
    
    if (isLastItemFilled && updatedItems.length === workItems.length) {
      setWorkItems([...updatedItems, { 
        id: generateUUID(), 
        partName: '', 
        quantity: '', 
        costPrice: '', 
        transactionAmount: '', 
        profit: '',
        hasInvoice: false,
        invoiceNumber: '',
        supplier: ''
      }]);
    }
  };

 

  const calculateTotalProfit = () => {
    return workItems.reduce((total, item) => {
      const profit = parseFloat(item.profit) || 0;
      return total + profit;
    }, 0).toFixed(2);
  };

  const calculateTotalAmount = () => {
    return workItems.reduce((total, item) => {
      const quantity = parseFloat(item.quantity) || 0;
      const pricePerUnit = parseFloat(item.transactionAmount) || 0;
      return total + (quantity * pricePerUnit);
    }, 0).toFixed(2);
  };

  const removeWorkItem = (id) => {
    if (workItems.length > 1) {
      setWorkItems(workItems.filter(item => item.id !== id));
    }
  };

  const today = new Date().toLocaleDateString('he-IL', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customerName || !formData.date) {
      toast({
        title: 'שגיאה',
        description: 'נא למלא לפחות שם לקוח ותאריך',
        variant: 'destructive'
      });
      return;
    }

    // Validate at least one work item with data
    const hasValidItem = workItems.some(item => 
      item.partName.trim() || item.quantity.trim() || item.costPrice.trim() || item.transactionAmount.trim()
    );
    
    if (!hasValidItem) {
      toast({
        title: 'שגיאה',
        description: 'נא למלא לפחות פריט עבודה אחד',
        variant: 'destructive'
      });
      return;
    }

    // Validate every entered amount has a payment method (otherwise it is dropped silently)
    if (findPaymentsMissingMethod(payments).length > 0) {
      toast({
        title: 'חסר אמצעי תשלום',
        description: 'מולא סכום תשלום בלי אמצעי תשלום. אם הלקוח שילם - בחר אמצעי תשלום (מזומן/שיק/אשראי/העברה). אם עדיין לא שילם - מחק את הסכום בשורת התשלום.',
        variant: 'destructive'
      });
      return;
    }

    // Validate check payments have a due date
    if (findChecksMissingDate(payments.filter(p => p.method && p.amount)).length > 0) {
      toast({
        title: 'שגיאה',
        description: 'חובה למלא תאריך פירעון לכל שיק',
        variant: 'destructive'
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Auto-save new customer if not in customers list
      const customerName = formData.customerName.trim();
      if (customerName) {
        const exists = allCustomers.some(c => `${c.firstName} ${c.lastName}`.trim().toLowerCase() === customerName.toLowerCase());
        if (!exists) {
          const spaceIdx = customerName.indexOf(' ');
          const firstName = spaceIdx > -1 ? customerName.substring(0, spaceIdx) : customerName;
          const lastName = spaceIdx > -1 ? customerName.substring(spaceIdx + 1) : '';
          try {
            await addCustomer({ firstName, lastName, phone: '', email: '', customerType: 'private' });
            loadAllCustomers();
          } catch (err) {
            console.error('Auto-save customer failed:', err);
          }
        }
      }

      const dataToSubmit = {
        ...formData,
        workItems: workItems.filter(item => item.partName.trim()), // Only send items with part names
        payments: payments.filter(p => p.method && p.amount), // Only send valid payments
        vatRate: vatState.effectiveRate
      };
      
      if (isEditMode) {
        await updateDailyLog(editId, dataToSubmit);
        setIsDirty(false); // Reset dirty flag after successful save
        toast({
          title: 'הצלחה!',
          description: 'הרישום היומי עודכן בהצלחה'
        });
      } else {
        await addDailyLog(dataToSubmit);
        setIsDirty(false); // Reset dirty flag after successful save
        toast({
          title: 'הצלחה!',
          description: 'הרישום היומי נוסף בהצלחה'
        });
        
        // Reset form for new entry
        const today = new Date();
        const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
        setFormData({
          customerName: '',
          date: todayString,
          payment: '',
          accountClosed: false
        });
        
        setWorkItems([
          { id: generateUUID(), partName: '', quantity: '', costPrice: '', transactionAmount: '', profit: '', hasInvoice: false, invoiceNumber: '', supplier: '' }
        ]);
        
        setPayments([
          { id: '1', method: '', amount: '', notes: '', checkNumber: '', checkDate: '', bankName: '', branch: '', accountNumber: '', paymentDate: '', hasReceipt: false }
        ]);
      }
      
      // If editing, go back to list
      if (isEditMode) {
        setTimeout(() => {
          navigate('/daily-logs');
        }, 500);
      }
    } catch (error) {
      toast({
        title: 'שגיאה',
        description: 'אירעה שגיאה בשמירת הרישום',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setIsDirty(true);
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-12 px-4" dir="rtl">
      <div className="container mx-auto max-w-4xl">
        <Card className="shadow-2xl border-0">
          <CardHeader className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-t-lg">
            <div className="flex justify-between items-center">
              <CardTitle className="text-3xl font-bold">
                {isEditMode ? 'עריכת רישום יומי' : 'רישום יומי חדש'}
              </CardTitle>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-3">
                  <Dialog open={isCalculatorOpen} onOpenChange={setIsCalculatorOpen}>
                    <DialogTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        className="border-white text-white hover:bg-white/20"
                      >
                        <Calculator className="w-4 h-4 ml-2" />
                        מחשבון
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md" dir="rtl">
                      <DialogHeader>
                        <DialogTitle className="text-2xl font-bold flex items-center gap-2">
                          <Calculator className="w-6 h-6 text-indigo-600" />
                          מחשבון
                        </DialogTitle>
                      </DialogHeader>
                      <div className="bg-white rounded-lg p-4">
                        <div className="bg-green-400 text-right text-black p-4 rounded-lg mb-4 text-3xl font-bold min-h-[60px] flex items-center justify-end break-all">
                          {calcDisplay}
                        </div>
                        <div className="grid grid-cols-4 gap-2">
                          <button type="button" onClick={handleCalcClear} className="col-span-2 bg-red-500 hover:bg-red-600 text-white font-bold py-4 rounded-lg">C</button>
                          <button type="button" onClick={() => handleCalcOperation('/')} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 rounded-lg">÷</button>
                          <button type="button" onClick={() => handleCalcOperation('*')} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 rounded-lg">×</button>
                          <button type="button" onClick={() => handleCalcNumber(7)} className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-4 rounded-lg">7</button>
                          <button type="button" onClick={() => handleCalcNumber(8)} className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-4 rounded-lg">8</button>
                          <button type="button" onClick={() => handleCalcNumber(9)} className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-4 rounded-lg">9</button>
                          <button type="button" onClick={() => handleCalcOperation('-')} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 rounded-lg">-</button>
                          <button type="button" onClick={() => handleCalcNumber(4)} className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-4 rounded-lg">4</button>
                          <button type="button" onClick={() => handleCalcNumber(5)} className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-4 rounded-lg">5</button>
                          <button type="button" onClick={() => handleCalcNumber(6)} className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-4 rounded-lg">6</button>
                          <button type="button" onClick={() => handleCalcOperation('+')} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 rounded-lg">+</button>
                          <button type="button" onClick={() => handleCalcNumber(1)} className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-4 rounded-lg">1</button>
                          <button type="button" onClick={() => handleCalcNumber(2)} className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-4 rounded-lg">2</button>
                          <button type="button" onClick={() => handleCalcNumber(3)} className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-4 rounded-lg">3</button>
                          <button type="button" onClick={handleCalcEquals} className="bg-green-500 hover:bg-green-600 text-white font-bold py-4 rounded-lg row-span-2">=</button>
                          <button type="button" onClick={() => handleCalcNumber(0)} className="col-span-2 bg-gray-700 hover:bg-gray-600 text-white font-bold py-4 rounded-lg">0</button>
                          <button type="button" onClick={() => handleCalcNumber('.')} className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-4 rounded-lg">.</button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                  <div className="flex items-center gap-2 text-sm bg-white/20 px-4 py-2 rounded-lg backdrop-blur-sm">
                    <Calendar className="w-4 h-4" />
                    <span>{today}</span>
                  </div>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Import from Deal Closure Button */}
              <div className="mb-4">
                <Dialog open={isImportDialogOpen} onOpenChange={setIsImportDialogOpen}>
                    <DialogTrigger asChild>
                      <Button
                        type="button"
                        onClick={() => loadDealClosures()}
                        className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white"
                      >
                        <FileDown className="ml-2 w-5 h-5" />
                        ייבוא מדווח סגירת עסקה
                      </Button>
                    </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" dir="rtl">
                    <DialogHeader>
                      <DialogTitle className="text-2xl font-bold">בחר עסקה לייבוא</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 mt-4">
                      {dealClosures.length === 0 ? (
                        <p className="text-center text-gray-500 py-8">אין עסקאות סגורות במערכת</p>
                      ) : (
                        dealClosures.map((deal) => (
                          <Card
                            key={deal.id}
                            className="cursor-pointer hover:shadow-lg transition-shadow border-2 hover:border-blue-500"
                            onClick={() => handleImportFromDealClosure(deal)}
                          >
                            <CardContent className="p-4">
                              <div className="flex justify-between items-start">
                                <div>
                                  <h3 className="text-lg font-bold text-gray-900">{deal.customerName}</h3>
                                  <p className="text-sm text-gray-600">תאריך: {deal.date}</p>
                                  <p className="text-sm text-gray-600">
                                    פריטים: {deal.items ? deal.items.length : 0}
                                  </p>
                                </div>
                                <Button
                                  type="button"
                                  size="sm"
                                  className="bg-blue-600 hover:bg-blue-700"
                                >
                                  בחר
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        ))
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              {/* Consolidated Invoice Payment Badge */}
              {consolidatedPaidNote && (
                <div className="bg-green-50 border-2 border-green-300 rounded-lg p-3 flex items-center gap-3" data-testid="consolidated-paid-badge">
                  <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-green-800">{consolidatedPaidNote}</p>
                  </div>
                </div>
              )}

              {/* Customer Name and Date Row */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2 relative">
                  <Label htmlFor="customerName" className="text-base font-semibold text-gray-700">
                    שם לקוח <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="customerName"
                    name="customerName"
                    value={formData.customerName}
                    onChange={(e) => {
                      handleChange(e);
                      const val = e.target.value.trim();
                      if (val.length > 0) {
                        const matches = allCustomers.filter(c => matchesCustomer(c, val));
                        setCustomerSuggestions(matches);
                        setShowSuggestions(matches.length > 0);
                      } else {
                        setShowSuggestions(false);
                      }
                    }}
                    onFocus={() => {
                      const val = (formData.customerName || '').trim();
                      if (val.length > 0) {
                        const matches = allCustomers.filter(c => matchesCustomer(c, val));
                        if (matches.length > 0) setShowSuggestions(true);
                      }
                    }}
                    onBlur={() => {
                      // Delay to allow click on suggestion
                      setTimeout(() => setShowSuggestions(false), 200);
                    }}
                    placeholder="הזן שם או מספר לקוח"
                    className="text-base py-5 border-2 focus:border-indigo-500 transition-colors"
                    required
                    autoComplete="off"
                    data-testid="customer-name-input"
                  />
                  {/* Autocomplete dropdown */}
                  {showSuggestions && customerSuggestions.length > 0 && (
                    <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border-2 border-indigo-300 rounded-lg shadow-xl max-h-48 overflow-y-auto" data-testid="customer-suggestions">
                      {customerSuggestions.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          className="w-full text-right px-4 py-3 hover:bg-indigo-50 transition-colors flex items-center justify-between border-b border-gray-100 last:border-0"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            const fullName = `${c.firstName} ${c.lastName}`.trim();
                            setFormData({ ...formData, customerName: fullName });
                            setShowSuggestions(false);
                            setIsDirty(true);
                            // Load customer balance
                            getCustomerBalance(fullName).then(bal => {
                              setCustomerBalanceInfo(Math.abs(bal.totalBalance) > 0.01 ? bal : null);
                            });
                          }}
                        >
                          <span className="text-sm font-medium text-gray-800"><span className="font-mono font-bold text-gray-500 ml-2">#{c.customerNumber}</span>{c.firstName} {c.lastName}</span>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full ${c.customerType === 'business' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                            {c.customerType === 'business' ? 'עסקי' : 'פרטי'}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Customer Balance Alert */}
                {customerBalanceInfo && (
                  <div className={`p-3 rounded-lg border-2 flex items-center gap-2 ${
                    customerBalanceInfo.totalBalance > 0 ? 'bg-red-50 border-red-300' : 'bg-blue-50 border-blue-300'
                  }`} data-testid="customer-balance-alert">
                    <span className={`font-bold text-sm ${customerBalanceInfo.totalBalance > 0 ? 'text-red-700' : 'text-blue-700'}`}>
                      {customerBalanceInfo.totalBalance > 0
                        ? `ללקוח יתרת חוב: ₪${customerBalanceInfo.totalBalance.toFixed(2)}`
                        : `ללקוח עודף: ₪${Math.abs(customerBalanceInfo.totalBalance).toFixed(2)}`}
                    </span>
                    <span className="text-xs text-gray-500">(מ-{customerBalanceInfo.invoiceCount} חשבונות מרוכזים)</span>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="date" className="text-base font-semibold text-gray-700">
                    תאריך <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="date"
                    name="date"
                    type="date"
                    value={formData.date}
                    onChange={handleChange}
                    className="text-base py-5 border-2 focus:border-indigo-500 transition-colors"
                    required
                  />
                </div>
              </div>

              {/* Work Items Table */}
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <Label className="text-lg font-bold text-gray-800">פירוט עבודה</Label>
                </div>
                
                <div className="overflow-x-auto border-2 border-gray-200 rounded-lg">
                  <table className="w-full border-collapse bg-white">
                    <thead>
                      <tr className="bg-gradient-to-r from-indigo-500 to-blue-500 text-white">
                        <th className="border border-gray-300 p-3 text-center w-[18%]">שם חלק/עבודה</th>
                        <th className="border border-gray-300 p-3 text-center w-[7%]">יש חשבונית</th>
                        <th className="border border-gray-300 p-3 text-center w-[10%]">מספר חשבונית</th>
                        <th className="border border-gray-300 p-3 text-center w-[8%]">כמות</th>
                        <th className="border border-gray-300 p-3 text-center w-[11%]">מחיר עלות</th>
                        <th className="border border-gray-300 p-3 text-center w-[11%]">מחיר ללקוח</th>
                        <th className="border border-gray-300 p-3 text-center w-[11%]">רווח</th>
                        <th className="border border-gray-300 p-3 text-center w-[13%]">ספק חלק</th>
                        <th className="border border-gray-300 p-3 text-center w-[6%]"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {workItems.map((item) => (
                        <tr key={item.id} className="hover:bg-blue-50 transition-colors">
                          <td className="border border-gray-300 p-2 relative">
                            <Textarea
                              value={item.partName}
                              onChange={(e) => {
                                handleItemChange(item.id, 'partName', e.target.value);
                                setActivePartRow(item.id);
                              }}
                              onFocus={() => setActivePartRow(item.id)}
                              onBlur={() => setTimeout(() => setActivePartRow(null), 200)}
                              placeholder="תאר את החלק/העבודה"
                              className="border-0 focus:ring-2 focus:ring-indigo-500 resize-none min-h-[60px]"
                              autoComplete="off"
                              data-testid={`dailylog-partname-${item.id}`}
                            />
                            {activePartRow === item.id && (() => {
                              const q = (item.partName || '').toLowerCase();
                              const matches = (q ? priceItems.filter(pi => (pi.partName || '').toLowerCase().includes(q)) : priceItems).slice(0, 50);
                              const hasText = !!(item.partName && item.partName.trim());
                              const exists = hasText && priceItems.some(pi => (pi.partName || '').trim().toLowerCase() === item.partName.trim().toLowerCase());
                              if (matches.length === 0 && !hasText) return null;
                              return (
                                <FloatingDropdown className="bg-white border-2 border-indigo-300 rounded-lg shadow-xl" data-testid={`dailylog-price-suggestions-${item.id}`}>
                                  {matches.map((pi) => (
                                    <button
                                      key={pi.id}
                                      type="button"
                                      className="w-full text-right px-4 py-2 hover:bg-indigo-50 transition-colors flex items-center justify-between border-b border-gray-100 last:border-0"
                                      onMouseDown={(e) => { e.preventDefault(); selectPriceItem(item.id, pi); }}
                                    >
                                      <span className="text-sm font-medium text-gray-800">{pi.partName}</span>
                                      {pi.price && <span className="text-sm font-bold text-indigo-700">₪{pi.price}</span>}
                                    </button>
                                  ))}
                                  {hasText && !exists && (
                                    <button
                                      type="button"
                                      className="w-full text-right px-4 py-2.5 bg-green-50 hover:bg-green-100 transition-colors flex items-center gap-2 text-green-800 font-semibold border-t border-green-200"
                                      onMouseDown={(e) => { e.preventDefault(); addItemToPriceList(item); }}
                                      data-testid={`dailylog-add-to-pricelist-${item.id}`}
                                    >
                                      <span className="text-lg leading-none">＋</span>
                                      <span className="text-sm">הוסף "{item.partName.trim()}" למחירון</span>
                                    </button>
                                  )}
                                </FloatingDropdown>
                              );
                            })()}
                          </td>
                          <td className="border border-gray-300 p-2 text-center">
                            <Checkbox
                              checked={item.hasInvoice || false}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  handleItemChange(item.id, 'hasInvoice', true);
                                  setTimeout(() => {
                                    setSelectedWorkItemForInvoice(workItems.find(i => i.id === item.id));
                                    setIsInvoiceSelectOpen(true);
                                  }, 0);
                                } else {
                                  const updatedItems = workItems.map(i => 
                                    i.id === item.id 
                                      ? { ...i, hasInvoice: false, invoiceNumber: '' }
                                      : i
                                  );
                                  setWorkItems(updatedItems);
                                }
                              }}
                            />
                          </td>
                          <td className="border border-gray-300 p-2">
                            <Input
                              value={item.invoiceNumber || ''}
                              onChange={(e) => handleItemChange(item.id, 'invoiceNumber', e.target.value)}
                              placeholder="מספר חשבונית"
                              className="border-0 focus:ring-2 focus:ring-indigo-500 text-center"
                              disabled={!item.hasInvoice}
                            />
                          </td>
                          <td className="border border-gray-300 p-2">
                            <Input
                              type="number"
                              step="0.01"
                              value={item.quantity}
                              onChange={(e) => handleItemChange(item.id, 'quantity', e.target.value)}
                              placeholder="0"
                              className="border-0 focus:ring-2 focus:ring-indigo-500 text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            />
                          </td>
                          <td className="border border-gray-300 p-2">
                            <Input
                              type="number"
                              step="0.01"
                              value={item.costPrice}
                              onChange={(e) => handleItemChange(item.id, 'costPrice', e.target.value)}
                              placeholder="0.00"
                              className="border-0 focus:ring-2 focus:ring-orange-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            />
                          </td>
                          <td className="border border-gray-300 p-2">
                            <Input
                              type="number"
                              step="0.01"
                              value={item.transactionAmount}
                              onChange={(e) => handleItemChange(item.id, 'transactionAmount', e.target.value)}
                              placeholder="0.00"
                              className="border-0 focus:ring-2 focus:ring-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            />
                          </td>
                          <td className="border border-gray-300 p-2">
                            <Input
                              type="number"
                              step="0.01"
                              value={item.profit}
                              readOnly
                              className="border-0 bg-green-50 font-semibold [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            />
                          </td>
                          <td className="border border-gray-300 p-2">
                            <select
                              value={item.supplier || ''}
                              onChange={(e) => handleItemChange(item.id, 'supplier', e.target.value)}
                              className="w-full p-2 border-0 focus:ring-2 focus:ring-indigo-500 rounded text-sm"
                            >
                              <option value="">בחר ספק...</option>
                              {suppliers.map((supplier) => (
                                <option key={supplier.id} value={supplier.name}>
                                  {supplier.name}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="border border-gray-300 p-2 text-center">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeWorkItem(item.id)}
                              disabled={workItems.length === 1}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Total Amount and Profit Display */}
                <div className="flex flex-col gap-3 items-end mt-4">
                  <div className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-6 py-3 rounded-lg shadow-lg">
                    <span className="text-lg font-bold">
                      סה"כ סכום עם הלקוח (לפני מע"מ): ₪{calculateTotalAmount()}
                    </span>
                  </div>
                  <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-3 rounded-lg shadow-lg">
                    <span className="text-lg font-bold">
                      סה"כ כולל רווח: ₪{calculateTotalProfit()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Payment Manager Component */}
              <PaymentManager
                totalAmount={withVat(subtotalAmount(), vatState.effectiveRate)}
                payments={payments}
                onPaymentsChange={setPayments}
                accountClosed={formData.accountClosed}
                onAccountClosedChange={(closed) => setFormData({ ...formData, accountClosed: closed })}
                disabled={false}
                bankAccounts={bankAccounts}
                suppliers={suppliers}
                consolidatedPaidNote={consolidatedPaidNote}
              />




              {/* Old payment sections removed - now handled by PaymentManager */}

              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <PrintButtons testIdPrefix="dailylog" docRef={editId ? { type: 'daily_log', recordId: editId } : null} getDoc={() => ({
                  title: 'רישום יומי',
                  customerName: formData.customerName,
                  date: formData.date,
                  items: workItems.filter(i => i.partName || i.transactionAmount).map(i => ({ name: i.partName, quantity: i.quantity, price: i.transactionAmount, cost: i.costPrice })),
                  payments: payments.filter(p => p.method && p.amount),
                  vatRate: vatState.effectiveRate
                })} />
                <VatSummary subtotal={subtotalAmount()} rate={vatState.effectiveRate} noVat={vatState.noVat} onNoVatChange={vatState.setNoVat} testIdPrefix="dailylog-vat" />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4 pt-6">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 py-6 text-lg font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  {isSubmitting ? 'שומר...' : 'שמור רישום'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleNavigation('/')}
                  className="px-8 py-6 text-lg border-2 hover:bg-gray-50 transition-colors"
                >
                  <ArrowRight className="ml-2 w-5 h-5" />
                  חזור
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
      
      {/* Invoice Gallery */}
      {selectedItem && (
        <InvoiceGallery
          isOpen={invoiceGalleryOpen}
          onClose={() => {
            setInvoiceGalleryOpen(false);
            setSelectedItem(null);
          }}
          itemId={selectedItem.id}
          itemName={selectedItem.partName || 'פריט'}
        />
      )}
      
      {/* Purchase Invoice Selection Dialog */}
      <Dialog open={isInvoiceSelectOpen} onOpenChange={setIsInvoiceSelectOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">בחר חשבונית קנייה</DialogTitle>
          </DialogHeader>
          
          <div className="mt-4">
            {purchaseInvoices.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                אין חשבוניות קנייה במערכת. יש להעלות חשבוניות דרך לשונית "חשבוניות קנייה".
              </div>
            ) : (
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full border-collapse bg-white">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-300 p-3 text-center w-16">בחר</th>
                      <th className="border border-gray-300 p-3 text-center">מספר חשבונית</th>
                      <th className="border border-gray-300 p-3 text-center">תאריך</th>
                      <th className="border border-gray-300 p-3 text-center">שם קובץ</th>
                      <th className="border border-gray-300 p-3 text-center w-32">פעולות</th>
                    </tr>
                  </thead>
                  <tbody>
                    {purchaseInvoices.map((invoice) => (
                      <tr key={invoice.id} className="hover:bg-blue-50 transition-colors">
                        <td className="border border-gray-300 p-3 text-center">
                          <Checkbox
                            checked={selectedWorkItemForInvoice?.invoiceNumber === invoice.invoiceNumber}
                            onCheckedChange={(checked) => {
                              if (checked && selectedWorkItemForInvoice) {
                                handleItemChange(selectedWorkItemForInvoice.id, 'invoiceNumber', invoice.invoiceNumber);
                                setIsInvoiceSelectOpen(false);
                                toast({
                                  title: 'הצלחה',
                                  description: `מספר חשבונית ${invoice.invoiceNumber} הועתק לפריט`
                                });
                              }
                            }}
                          />
                        </td>
                        <td className="border border-gray-300 p-3 text-center font-bold text-purple-700">
                          {invoice.invoiceNumber}
                        </td>
                        <td className="border border-gray-300 p-3 text-center">
                          {invoice.invoiceDate}
                        </td>
                        <td className="border border-gray-300 p-3 text-center text-sm text-gray-600">
                          {invoice.fileName}
                        </td>
                        <td className="border border-gray-300 p-3">
                          <div className="flex justify-center">
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                const url = getPurchaseInvoiceDownloadUrl(invoice.id);
                                const width = 900;
                                const height = 700;
                                const left = (window.screen.width - width) / 2;
                                const top = (window.screen.height - height) / 2;
                                window.open(
                                  url,
                                  'InvoiceViewer',
                                  `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,toolbar=no,menubar=no,location=no,status=no`
                                );
                              }}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              title="צפה בחשבונית"
                            >
                              <Eye className="w-4 h-4 ml-1" />
                              צפייה
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            <div className="flex justify-end mt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setIsInvoiceSelectOpen(false);
                  setSelectedWorkItemForInvoice(null);
                }}
              >
                סגור
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Customer Dialogs */}
      <BusinessCustomerDialog
        open={isBusinessCustomerDialogOpen}
        onClose={() => setIsBusinessCustomerDialogOpen(false)}
        onSelectCustomer={handleSelectBusinessCustomer}
      />
      <PrivateCustomerDialog
        open={isPrivateCustomerDialogOpen}
        onClose={() => setIsPrivateCustomerDialogOpen(false)}
        onSelectCustomer={handleSelectPrivateCustomer}
      />

      {/* Exit Confirmation Dialog */}
      <Dialog open={showExitDialog} onOpenChange={setShowExitDialog}>
        <DialogContent className="max-w-md" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-orange-800">
              האם לצאת בלי לשמור?
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-lg text-gray-700">
              יש לך שינויים שלא נשמרו. האם אתה בטוח שברצונך לצאת?
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={cancelExit}
              className="flex-1 py-6 text-lg bg-gradient-to-r from-green-600 to-emerald-600"
            >
              המשך בעריכה
            </Button>
            <Button
              onClick={confirmExit}
              variant="outline"
              className="flex-1 py-6 text-lg border-2 border-red-500 text-red-600 hover:bg-red-50"
            >
              צא בלי לשמור
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DailyLog;